import { useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { loadSession } from '../lib/api';

/**
 * "A new version is ready" — asked, never imposed.
 *
 * THE BUG BEHIND THIS was a cache header, not this component: /sw.js was served
 * with max-age=86400, so a browser was entitled to reuse the service worker —
 * and therefore the whole precached bundle — for a day after a release. That is
 * fixed in src/http/static.ts, and on its own it is enough for anyone who
 * reloads.
 *
 * This is for the tab nobody reloads. A property manager leaves the arrears
 * screen open all morning; without a check on a timer they would keep the old
 * bundle until they closed it.
 *
 * WHY IT ASKS INSTEAD OF RELOADING. The plugin was set to `autoUpdate`, which
 * swaps the worker and reloads the page on its own. On a marketing site that is
 * invisible and right. Here somebody may be twenty lines into an invoice when
 * the deploy lands, and a silent reload would take the lot. So the new worker
 * waits, a quiet bar appears, and the refresh happens when the person says so.
 *
 * The bar is deliberately small and bottom-left: it must not sit under the
 * assistant's launcher, and it is never urgent enough to interrupt.
 *
 * EXCEPT FOR A VISITOR WHO IS NOT SIGNED IN, who is the case the paragraph
 * above already concedes: "on a marketing site that is invisible and right".
 * Somebody reading the pricing page has nothing half-typed to lose, and asking
 * them to press Refresh means the page they are reading stays the old one until
 * they do — so a change to the landing page reaches returning visitors only if
 * they act on a bar they have no reason to care about. For them the update is
 * applied silently. The prompt is kept for exactly the person it was written
 * for: somebody signed in, twenty lines into an invoice.
 *
 * The test is `loadSession()` rather than `useAuth().isAuthenticated`, and that
 * matters: isAuthenticated is `!!session` and reads FALSE while /me is still in
 * flight, so a signed-in landlord would be briefly indistinguishable from a
 * visitor — and briefly is all it takes to reload somebody's work away.
 * loadSession reads localStorage synchronously and is never in that state.
 *
 * BUT "NOT SIGNED IN" IS NOT THE SAME AS "NOTHING TO LOSE", which is what the
 * paragraph above assumed. Nine public pages take typed input, and the one that
 * matters most is the buyer enquiry on a sale listing: somebody part-way
 * through asking about a house, which is the single conversion this product
 * advertises for. ListingDetail, RequestDemo, RequestQuote, ShareStory,
 * ServiceRegister, ProviderSignup and Register are all the same shape. A silent
 * reload there discards what they wrote, with no bar and no undo — worse than
 * the bug it replaced, which at least still sent the message.
 *
 * So the question asked is "is there work in progress", not "who is this".
 * Signed in, or anything typed and not yet submitted: the bar. Otherwise, and
 * that is the marketing page the silent update was written for, apply it.
 */

/**
 * Anything the person has typed and not yet sent.
 *
 * Deliberately crude — any non-empty field counts, including one they typed
 * into and abandoned. Erring towards showing a bar costs a click; erring the
 * other way costs somebody's enquiry. A placeholder is not a value and a
 * disabled or read-only field is not theirs to lose, so neither counts.
 */
function hasTypedInput(): boolean {
  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'input, textarea');
  for (const f of fields) {
    if (f.disabled || f.readOnly) continue;
    /* Checkboxes and radios carry a value attribute whether or not they are
       ticked, and a search box the browser autofilled is not work in progress
       the way a typed message is. Text is what somebody would mourn. */
    if (f instanceof HTMLInputElement
        && ['checkbox', 'radio', 'hidden', 'submit', 'button'].includes(f.type)) continue;
    if (f.value.trim() !== '') return true;
  }
  return [...document.querySelectorAll('[contenteditable="true"]')]
    .some(el => (el.textContent ?? '').trim() !== '');
}
export function UpdateBanner() {
  const [ready, setReady] = useState(false);
  const [update, setUpdate] = useState<(() => Promise<void>) | null>(null);

  useEffect(() => {
    const updateSW = registerSW({
      onNeedRefresh() {
        /*
         * Re-read at the moment the update lands, not at mount: somebody can
         * sign in between the page loading and a release going out, and the
         * question is whether they have work in progress NOW.
         */
        if (!loadSession() && !hasTypedInput()) { void updateSW(true); return; }
        // Wrapped in a function so setState stores it rather than calling it.
        setUpdate(() => () => updateSW(true));
        setReady(true);
      },
      onRegisteredSW(_url, registration) {
        if (!registration) return;
        /*
         * Ask hourly. The check is one conditional request for a file now
         * served no-cache, so it is a 304 every time until a release actually
         * lands. Cheaper than the support conversation that starts "I'm still
         * seeing the old screen".
         */
        const timer = setInterval(() => { void registration.update(); }, 60 * 60 * 1000);
        // Also the moment somebody comes back to the tab, which is when they
        // are about to act on what it shows.
        const onVisible = () => { if (document.visibilityState === 'visible') void registration.update(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
      },
    });
  }, []);

  if (!ready) return null;

  return (
    <div className="sw-update" role="status">
      <span>A new version of NyumbaSuite is ready.</span>
      <button type="button" onClick={() => void update?.()}>Refresh</button>
      <button type="button" className="sw-update-later" onClick={() => setReady(false)} aria-label="Dismiss">
        Later
      </button>
    </div>
  );
}
